import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string | null;
  price_monthly: number;
  price_annual: number;
  price_lifetime: number;
  stripe_product_id: string | null;
  stripe_price_monthly_id: string | null;
  stripe_price_annual_id: string | null;
  stripe_price_lifetime_id: string | null;
  max_users: number;
  max_integrations: number;
  integrations_mode: 'predefined' | 'customizable';
  allowed_integrations: string[];
  basic_metrics: string[];
  advanced_metrics_enabled: boolean;
  advanced_metrics_mode: 'predefined' | 'customizable';
  advanced_metrics: string[];
  max_advanced_metrics: number;
  goals_enabled: boolean;
  max_goals: number;
  manual_entry_enabled: boolean;
  bulk_import_enabled: boolean;
  priority_support: boolean;
  is_active: boolean;
  inactivated_at?: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export function usePlans() {
  const queryClient = useQueryClient();

  const { data: plans, isLoading } = useQuery({
    queryKey: ['subscription_plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as SubscriptionPlan[];
    },
  });

  const createPlan = useMutation({
    mutationFn: async (plan: Omit<SubscriptionPlan, 'id' | 'created_at' | 'updated_at' | 'display_order'>) => {
      // Get the highest display_order and add 1
      const { data: existingPlans } = await supabase
        .from('subscription_plans')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      const newDisplayOrder = existingPlans && existingPlans.length > 0 
        ? existingPlans[0].display_order + 1 
        : 1;

      const { error } = await supabase
        .from('subscription_plans')
        .insert({ ...plan, display_order: newDisplayOrder });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription_plans'] });
      toast.success('Plano criado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao criar plano: ' + error.message);
    },
  });

  const updatePlan = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SubscriptionPlan> & { id: string }) => {
      const { error } = await supabase
        .from('subscription_plans')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription_plans'] });
      toast.success('Plano atualizado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar plano: ' + error.message);
    },
  });

  const deletePlan = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('subscription_plans')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription_plans'] });
      toast.success('Plano excluído com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao excluir plano: ' + error.message);
    },
  });

  const togglePlanStatus = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const updateData: { is_active: boolean; inactivated_at?: string | null } = { 
        is_active,
        inactivated_at: is_active ? null : new Date().toISOString()
      };

      const { error } = await supabase
        .from('subscription_plans')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription_plans'] });
      toast.success('Status do plano atualizado!');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar status: ' + error.message);
    },
  });

  const updatePlanOrder = useMutation({
    mutationFn: async (updates: { id: string; display_order: number }[]) => {
      const promises = updates.map(({ id, display_order }) =>
        supabase
          .from('subscription_plans')
          .update({ display_order })
          .eq('id', id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);
      
      if (errors.length > 0) {
        throw errors[0].error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription_plans'] });
      toast.success('Ordem atualizada com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar ordem: ' + error.message);
    },
  });

  return {
    plans,
    isLoading,
    createPlan,
    updatePlan,
    deletePlan,
    togglePlanStatus,
    updatePlanOrder,
  };
}
